// Centralized Bonus Service with caching and smart refresh
// Replaces aggressive polling with event-driven updates

import { supabase } from './supabaseClient';

export interface ClaimableBonus {
  id: string;
  bonus_type: string;
  points: number;
  rule_name: string;
  description: string;
  created_at: string;
  expires_at: string | null;
  notification_shown: boolean;
}

interface CachedBonusData {
  bonuses: ClaimableBonus[];
  fetchedAt: number;
  userId: string;
}

class BonusService {
  private static instance: BonusService;
  private cache: CachedBonusData | null = null;
  private fetchInProgress: Promise<ClaimableBonus[]> | null = null;
  private listeners: Set<(bonuses: ClaimableBonus[]) => void> = new Set();
  
  // Cache duration: 30 minutes (bonuses don't change frequently)
  private readonly CACHE_DURATION_MS = 30 * 60 * 1000;
  
  // Daily bonus check tracking
  private lastDailyCheckDate: Map<string, string> = new Map();
  private dailyCheckInProgress: Map<string, Promise<boolean>> = new Map();

  static getInstance(): BonusService {
    if (!BonusService.instance) {
      BonusService.instance = new BonusService();
    }
    return BonusService.instance;
  }

  /**
   * Subscribe to bonus updates
   */
  subscribe(callback: (bonuses: ClaimableBonus[]) => void): () => void {
    this.listeners.add(callback);
    // Immediately call with cached data if available
    if (this.cache) {
      callback(this.cache.bonuses);
    }
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(bonuses: ClaimableBonus[]) {
    this.listeners.forEach(cb => cb(bonuses));
  }

  /**
   * Check daily login bonus with deduplication (once per day per user)
   */
  async checkDailyLoginBonus(userId: string): Promise<boolean> {
    const today = new Date().toDateString();

    // Already checked today
    if (this.lastDailyCheckDate.get(userId) === today) {
      return false;
    }

    // Check in progress
    if (this.dailyCheckInProgress.has(userId)) {
      return this.dailyCheckInProgress.get(userId)!;
    }

    const checkPromise = this._performDailyBonusCheck(userId);
    this.dailyCheckInProgress.set(userId, checkPromise);

    try {
      const result = await checkPromise;
      if (result) {
        this.lastDailyCheckDate.set(userId, today);
        // Invalidate cache since a new bonus was created
        this.invalidateCache();
      }
      return result;
    } finally {
      this.dailyCheckInProgress.delete(userId);
    }
  }

  private async _performDailyBonusCheck(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('check_daily_login_bonus', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error checking daily login bonus:', error);
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Exception in daily bonus check:', err);
      return false;
    }
  }

  /**
   * Get claimable bonuses with caching
   */
  async getClaimableBonuses(userId: string, forceRefresh = false): Promise<ClaimableBonus[]> {
    // Return cached data if valid
    if (!forceRefresh && this.isCacheValid(userId)) {
      return this.cache!.bonuses;
    }

    // Deduplicate concurrent requests
    if (this.fetchInProgress) {
      return this.fetchInProgress;
    }

    this.fetchInProgress = this._fetchBonuses(userId);

    try {
      const bonuses = await this.fetchInProgress;
      this.cache = {
        bonuses,
        fetchedAt: Date.now(),
        userId
      };
      this.notifyListeners(bonuses);
      return bonuses;
    } finally {
      this.fetchInProgress = null;
    }
  }

  private async _fetchBonuses(userId: string): Promise<ClaimableBonus[]> {
    try {
      const { data, error } = await supabase.rpc('get_claimable_bonuses', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error fetching claimable bonuses:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Exception fetching bonuses:', err);
      return [];
    }
  }

  private isCacheValid(userId: string): boolean {
    if (!this.cache) return false;
    if (this.cache.userId !== userId) return false;
    
    const age = Date.now() - this.cache.fetchedAt;
    return age < this.CACHE_DURATION_MS;
  }

  /**
   * Invalidate cache - call after claiming a bonus or when day changes
   */
  invalidateCache() {
    this.cache = null;
  }

  /**
   * Check if there are claimable bonuses (uses cache)
   */
  hasClaimableBonuses(): boolean {
    return this.cache ? this.cache.bonuses.length > 0 : false;
  }

  /**
   * Get cached bonuses count without fetching
   */
  getCachedBonusCount(): number {
    return this.cache?.bonuses.length ?? 0;
  }

  /**
   * Called when user claims a bonus - invalidates cache and refreshes
   */
  async onBonusClaimed(userId: string): Promise<ClaimableBonus[]> {
    this.invalidateCache();
    return this.getClaimableBonuses(userId, true);
  }

  /**
   * Reset service state (call on logout)
   */
  reset() {
    this.cache = null;
    this.fetchInProgress = null;
    this.lastDailyCheckDate.clear();
    this.dailyCheckInProgress.clear();
    this.listeners.clear();
  }

  /**
   * Cleanup old daily check entries
   */
  cleanup() {
    const today = new Date().toDateString();
    for (const [userId, date] of this.lastDailyCheckDate.entries()) {
      if (date !== today) {
        this.lastDailyCheckDate.delete(userId);
      }
    }
  }
}

export default BonusService;
